import { AnyAction, Dispatch } from 'redux';

import { resetAddressFields, updateField } from '../../store/customer';
import { initialState } from '../../store/customer/state.customer';
import { clearFieldError, setErrorFields } from '../../store/fieldError';

interface FormikWithForm {
  form: {
    setFieldValue: (field: string, value: unknown) => Promise<void>;
    setFieldTouched: (field: string, touched: unknown) => Promise<void>;
    validateForm?: () => Promise<Record<string, unknown>>;
  };
}

// This function updates the value for useSoldTo, useBillTo, and usePayer of the address in the store
/**
 * @param {function} dispatch - Triggers reducers and updates the store based on action
 * @param {Object} formik - Formik object with methods to update form and validation
 * @param {string} name - Property name you want to update in the store
 * @param {bool} useSoldTo - New value for the useSoldTo property
 * @param {bool} useBillTo - New value for the useBillTo property
 * @param {bool} usePayer - New value for the usePayer property
 */
export async function toggleUseTo(
  dispatch: Dispatch<AnyAction>,
  formik: FormikWithForm,
  name: string,
  useSoldTo: boolean,
  useBillTo: boolean,
  usePayer: boolean
): Promise<void> {
  const isReviewPage = window.location.href.includes('review');
  const isDifferent = !useBillTo && !useSoldTo && !usePayer;
  if (isDifferent) {
    await formik.form.setFieldValue(name, initialState.address);
    await formik.form.setFieldTouched(name, {});
    if (!isReviewPage) {
      dispatch(clearFieldError(name));
    }
    dispatch(resetAddressFields(name));
  }
  dispatch(updateField({ name: `${name}.useSoldTo`, value: useSoldTo }));
  dispatch(updateField({ name: `${name}.useBillTo`, value: useBillTo }));
  dispatch(updateField({ name: `${name}.usePayer`, value: usePayer }));
  await formik.form.setFieldValue(`${name}.useSoldTo`, useSoldTo);
  await formik.form.setFieldValue(`${name}.useBillTo`, useBillTo);
  await formik.form.setFieldValue(`${name}.usePayer`, usePayer);
  const isSameAs = useBillTo || useSoldTo || usePayer;
  if (isSameAs) {
    const category = name && name.split(',')[0];
    dispatch(clearFieldError(category));
  }

  // In review mode, immediately repopulate field errors after toggle updates.
  // This prevents a transient state where card errors disappear and header flips back to Edit.
  if (isReviewPage && typeof formik?.form?.validateForm === 'function') {
    const nextErrors = await formik.form.validateForm();
    dispatch(setErrorFields(nextErrors || {}));
  }
}

export function traverse<T extends Record<string, unknown>>(o: T, func: (key: string, value: unknown) => void): void {
  for (const i in o) {
    // Only own properties to avoid walking (and polluting via) the prototype chain
    if (!Object.prototype.hasOwnProperty.call(o, i) || i === '__proto__' || i === 'constructor' || i === 'prototype') {
      continue;
    }
    func.apply(null, [i, o[i]]);
    if (o[i] !== null && typeof o[i] == 'object') {
      //going one step down in the object tree!!`
      traverse(o[i] as Record<string, unknown>, func);
    }
  }
}
/**
 * Returns boolean if name contain address
 * @param {name} field path
 */
export function isAddressField(name = ''): boolean {
  const pathArray = name.split('.');
  if (pathArray.length > 1) {
    return pathArray[1] === 'address';
  }
  return false;
}
/**
 * Returns boolean same address is selected
 * @param {name} field path
 * * @param {customer} data path
 */
export function isSameAsSelected(name: string, customer: Record<string, unknown>): boolean {
  const requestedPath = name.slice(0, name.lastIndexOf('.'));
  const addressObject = (objectByString(requestedPath, customer) as { useSoldTo?: boolean; useBillTo?: boolean }) || {};
  const { useSoldTo = false, useBillTo = false } = addressObject;
  return useSoldTo || useBillTo;
}
/**
 * Returns requested deep object
 * @param {o} object
 * @param {s} field path
 */
export function objectByString(s: string, o: Record<string, unknown> = {}): unknown {
  if (s === '') {
    return o; // Return the entire object if an empty string is provided
  }

  s = s.replace(/\[(\w+)\]/g, '.$1'); // convert indexes to properties
  s = s.replace(/^\./, ''); // strip a leading dot
  const a = s.split('.');
  for (let i = 0, n = a.length; i < n; ++i) {
    const k = a[i];
    if (k === '__proto__' || k === 'constructor' || k === 'prototype') {
      return null; // guard against prototype pollution
    }
    if (Object.prototype.hasOwnProperty.call(o, k)) {
      o = o[k] as Record<string, unknown>;
    } else {
      return null;
    }
  }
  return o;
}

export function getCardAndAttributeByName(path: string): { cardId: string; index: string | number; attribute: string } {
  const nameArray = path.split('.');
  const attribute = nameArray[nameArray.length - 1];
  let name = nameArray[0];
  let cardId = name;
  let index: string | number = 0;
  if (name.includes(']')) {
    cardId = name.replace(/[[\]']+/g, '').replace(/\d/g, '');
    index = name.replace(/[[\]']+/g, '').replace(/^\D+/g, '');
  }
  return { cardId, index, attribute };
}

export function contactDeliveryType(name: string, dispatch: Dispatch<AnyAction>, value?: string): void {
  let contactCardNames = ['coa', 'packingList', 'orderConfirmation', 'invoiceDelivery'];
  let presentString: string | null = null;
  let replacement = 'contactType';
  let lastDotIndex: number;
  let updatedContactType: string;
  let startIndex: number;
  let contactTypeValue: string;
  contactCardNames.forEach(string => {
    if (name?.includes(string)) {
      presentString = string;
    }
    return presentString;
  });

  lastDotIndex = name.lastIndexOf('.');
  updatedContactType = name.substring(0, lastDotIndex + 1) + replacement;

  startIndex = name.lastIndexOf('.') + 1;
  contactTypeValue = name.substring(startIndex);
  if (value === 'N/A') {
    dispatch(
      updateField({
        name: updatedContactType,
        value: value
      })
    );
  } else if (value === undefined) {
    dispatch(
      updateField({
        name: updatedContactType,
        value: contactTypeValue.charAt(0).toUpperCase() + contactTypeValue?.slice(1)
      })
    );
  }
}
